import React, {useState, useEffect, useMemo, useRef} from 'react';
import {useNavigate} from 'react-router';
import {
    Box,
    Typography,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Button,
    Grid,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Avatar,
    Stack,
    Tabs,
    Tab
} from '@mui/material';
import {
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    ArrowBack as BackIcon,
    Clear as ClearIcon,
    FilterListOff as FilterListOffIcon,
    PersonAdd as PersonAddIcon,
    AdminPanelSettings as AdminIcon,
    School as TeacherIcon,
    Person as StudentIcon,
    Block as BlockIcon,
    CheckCircle as ActiveIcon,
    Timeline as ActivityIcon
} from '@mui/icons-material';
import {createApiClient} from '../../../services/ApiClient.jsx';
import ActivityLogs from './ActivityLogs.jsx';
import ProfileDetails from './ProfileDetails.jsx';

const UserManagement = ({accessToken, onBack, standalone = false}) => {
    const navigate = useNavigate();

    // State management
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [userLogs, setUserLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // Ref to access ProfileDetails save function
    const profileDetailsRef = useRef(null);

    // Create API client
    const apiClient = useMemo(() => {
        if (!accessToken) {
            console.error('No access token provided to UserManagement');
            return null;
        }
        const client = createApiClient(accessToken);
        console.log('Created API client:', client);
        return client;
    }, [accessToken]);

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return searchTerm || roleFilter || statusFilter;
    }, [searchTerm, roleFilter, statusFilter]);

    // Fetch users
    const fetchUsers = async (showLoading = true) => {
        if (!apiClient) {
            console.error('No API client available for fetchUsers');
            return;
        }

        try {
            if (showLoading) {
                setLoading(true);
            }
            setError(null);

            console.log('Fetching users...');
            const response = await apiClient.get('/api/users/', {
                limit: 1000, // Get all users for admin
                offset: 0
            });
            console.log('Users fetch response:', response);

            const userData = response?.data || response || [];
            console.log('Processed user data:', userData);
            setUsers(userData);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            setError(error.message || 'Failed to load users');
            setUsers([]);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    // Initial data load
    useEffect(() => {
        console.log('UserManagement useEffect - apiClient:', apiClient);
        if (apiClient) {
            fetchUsers();
        }
    }, [apiClient]);

    // Handle back navigation
    const handleBackClick = () => {
        if (standalone) {
            navigate('/admin/dashboard');
        } else if (onBack) {
            onBack();
        }
    };

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchTerm('');
        setRoleFilter('');
        setStatusFilter('');
        setPage(0);
    };

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshing(true);
        setPage(0);
        await fetchUsers();
        setRefreshing(false);
    };

    // Fetch user logs from Logto
    const fetchUserLogs = async (userId) => {
        if (!apiClient || !userId) return;

        try {
            setLoadingLogs(true);

            const response = await apiClient.get(`/api/audit/logs/user/${userId}/all`, {
                max_pages: 3 // Limit to first 3 pages to avoid excessive data
            });

            setUserLogs(response.data || []);
        } catch (error) {
            console.error('Failed to fetch user logs:', error);
            setUserLogs([]);
        } finally {
            setLoadingLogs(false);
        }
    };

    // Helper function to check if user has a specific role
    const userHasRole = (user, role) => {
        return user.roles && user.roles.includes(role);
    };

    // Enhanced function to get all user roles using the roles array
    const getUserRoles = (user) => {
        const roles = [];
        const userRoles = user.roles || [];

        if (userRoles.includes('admin')) {
            roles.push({label: 'Admin', color: 'error', icon: AdminIcon});
        }
        if (userRoles.includes('teacher')) {
            roles.push({label: 'Teacher', color: 'primary', icon: TeacherIcon});
        }
        if (userRoles.includes('student')) {
            roles.push({label: 'Student', color: 'default', icon: StudentIcon});
        }

        // If no roles specified at all, default to student
        if (userRoles.length === 0) {
            roles.push({label: 'Student', color: 'default', icon: StudentIcon});
        }

        return roles;
    };

    // Get user status display
    const getUserStatus = (user) => {
        if (user.soft_deleted) return {label: 'Deleted', color: 'error', icon: BlockIcon};
        if (user.is_suspended) return {label: 'Suspended', color: 'warning', icon: BlockIcon};
        return {label: 'Active', color: 'success', icon: ActiveIcon};
    };

    // Role statistics helper using roles array
    const getRoleStatistics = (users) => {
        const stats = {
            admins: users.filter(u => userHasRole(u, 'admin')).length,
            teachers: users.filter(u => userHasRole(u, 'teacher')).length,
            students: users.filter(u => {
                const roles = u.roles || [];
                return roles.includes('student') || roles.length === 0;
            }).length
        };
        return stats;
    };

    // Enhanced filtering using roles array
    const filteredUsers = useMemo(() => {
        let filtered = users;

        // Text search
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(user =>
                user.username?.toLowerCase().includes(term) ||
                user.email?.toLowerCase().includes(term) ||
                user.id?.toLowerCase().includes(term) ||
                user.roles?.some(role => role.toLowerCase().includes(term))
            );
        }

        // Role filter using roles array
        if (roleFilter) {
            filtered = filtered.filter(user => {
                const roles = user.roles || [];

                if (roleFilter === 'admin') return roles.includes('admin');
                if (roleFilter === 'teacher') return roles.includes('teacher');
                if (roleFilter === 'student') return roles.includes('student') || roles.length === 0;
                return true;
            });
        }

        // Status filter
        if (statusFilter) {
            if (statusFilter === 'active') {
                filtered = filtered.filter(user => !user.is_suspended && !user.soft_deleted);
            } else if (statusFilter === 'suspended') {
                filtered = filtered.filter(user => user.is_suspended);
            } else if (statusFilter === 'deleted') {
                filtered = filtered.filter(user => user.soft_deleted);
            }
        }

        return filtered;
    }, [users, searchTerm, roleFilter, statusFilter]);

    // Paginated users
    const paginatedUsers = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredUsers, page, rowsPerPage]);

    // Role statistics
    const roleStats = useMemo(() => getRoleStatistics(filteredUsers), [filteredUsers]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            return new Date(timestamp).toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    // Handle edit user - opens Profile Details tab
    const handleEditUser = (user) => {
        console.log('handleEditUser called with:', user);
        setSelectedUser(user);
        setActiveTab(0); // Profile Details tab (was switched)
        setEditDialogOpen(true);
    };

    // Handle view user activity - opens Activity Logs tab
    const handleViewUser = (user) => {
        console.log('handleViewUser called with:', user);
        setSelectedUser(user);
        setActiveTab(1); // Activity Logs tab (was switched)
        setEditDialogOpen(true);
        setUserLogs([]); // Clear previous logs
        
        // Fetch logs immediately since we're opening the Activity Logs tab
        fetchUserLogs(user.id);
    };

    // Handle user update from ProfileDetails component
    const handleUserUpdate = (updatedUser) => {
        console.log('handleUserUpdate called with:', updatedUser);
        // Update the user in the users array
        setUsers(prevUsers =>
            prevUsers.map(user =>
                user.id === updatedUser.id ? updatedUser : user
            )
        );
        // Update selected user
        setSelectedUser(updatedUser);
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        
        // Fetch logs when switching to Activity Logs tab (tab 1)
        if (newValue === 1 && selectedUser && userLogs.length === 0) {
            fetchUserLogs(selectedUser.id);
        }
    };


    // Component for displaying multiple roles
    const RoleCell = ({user}) => {
        const roles = getUserRoles(user);

        if (roles.length === 1) {
            const role = roles[0];
            const RoleIcon = role.icon;
            return (
                <Chip
                    label={role.label}
                    color={role.color}
                    size="small"
                    icon={<RoleIcon/>}
                />
            );
        }

        // Multiple roles - stack them
        return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {roles.map((role, index) => {
                    const RoleIcon = role.icon;
                    return (
                        <Chip
                            key={index}
                            label={role.label}
                            color={role.color}
                            size="small"
                            icon={<RoleIcon/>}
                            sx={{mb: 0.5}}
                        />
                    );
                })}
            </Stack>
        );
    };

    if (error) {
        return (
            <Box sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                <Container maxWidth="xl" sx={{pt: 4, pb: 2}}>
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                    <Button onClick={handleBackClick} startIcon={<BackIcon/>}>
                        Back to Dashboard
                    </Button>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{
            height: '87vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <Container maxWidth="xl" sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                pt: 4,
                pb: 2,
                overflow: 'hidden'
            }}>
                {/* Debug Info */}
                {!apiClient && (
                    <Alert severity="warning" sx={{mb: 2}}>
                        API Client not available. Access Token: {accessToken ? 'Available' : 'Missing'}
                    </Alert>
                )}

                {/* Header */}
                <Box
                    sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <IconButton onClick={handleBackClick}>
                            <BackIcon/>
                        </IconButton>
                        <Typography variant="h4" component="h1">
                            User Management
                        </Typography>
                        {hasActiveFilters && (
                            <Chip
                                label="Filtered"
                                color="primary"
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={refreshing ? <CircularProgress size={16}/> : <RefreshIcon/>}
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Box>

                {/* Filters */}
                <Paper sx={{p: 3, mb: 3, flexShrink: 0}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                        <Typography variant="h6">
                            <FilterIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
                            Filters
                        </Typography>
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<FilterListOffIcon/>}
                                onClick={handleClearFilters}
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{xs: 12, md: 4}}>
                            <TextField
                                fullWidth
                                label="Search Users"
                                placeholder="Username, email, ID, or role"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    endAdornment: searchTerm && (
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            <ClearIcon/>
                                        </IconButton>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid size={{xs: 12, md: 4}}>
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select
                                    value={roleFilter}
                                    label="Role"
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                >
                                    <MenuItem value="">All Roles</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                    <MenuItem value="teacher">Teacher</MenuItem>
                                    <MenuItem value="student">Student</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{xs: 12, md: 4}}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="suspended">Suspended</MenuItem>
                                    <MenuItem value="deleted">Deleted</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Results Info */}
                <Box
                    sx={{mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0}}>
                    <Typography variant="body2" color="text.secondary">
                        {loading ? 'Loading...' : (
                            `Showing ${paginatedUsers.length} of ${filteredUsers.length} users (Total: ${users.length})`
                        )}
                        {hasActiveFilters && ` [Filters active]`}
                    </Typography>

                    {!loading && (
                        <Stack direction="row" spacing={1}>
                            <Chip label={`${roleStats.admins} Admins`} size="small" color="error" variant="outlined"/>
                            <Chip label={`${roleStats.teachers} Teachers`} size="small" color="primary"
                                  variant="outlined"/>
                            <Chip label={`${roleStats.students} Students`} size="small" variant="outlined"/>
                        </Stack>
                    )}
                </Box>

                {/* Users Table - Scrollable Container */}
                <Paper sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <TableContainer sx={{
                        flex: 1,
                        overflow: 'auto'
                    }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Onboarding</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{py: 4}}>
                                            <CircularProgress/>
                                            <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                                Loading users...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedUsers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{py: 4}}>
                                            <Typography variant="body1" color="text.secondary">
                                                {users.length === 0 ? 'No users found' : 'No users match your filters'}
                                            </Typography>
                                            {hasActiveFilters && (
                                                <Button
                                                    size="small"
                                                    onClick={handleClearFilters}
                                                    sx={{mt: 1}}
                                                >
                                                    Clear filters
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedUsers.map((user) => {
                                        const status = getUserStatus(user);
                                        const StatusIcon = status.icon;

                                        return (
                                            <TableRow key={user.id} hover>
                                                <TableCell>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                                        <Avatar sx={{width: 32, height: 32}}>
                                                            {user.username?.charAt(0)?.toUpperCase() || 'U'}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {user.username || 'N/A'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {user.email || user.id}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <RoleCell user={user}/>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={status.label}
                                                        color={status.color}
                                                        size="small"
                                                        icon={<StatusIcon/>}
                                                        variant={status.label === 'Active' ? 'outlined' : 'filled'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={user.onboarding_complete ? 'Complete' : 'Pending'}
                                                        color={user.onboarding_complete ? 'success' : 'default'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatTimestamp(user.created_at)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={0.5}>
                                                        <Tooltip title="Edit Profile">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditUser(user)}
                                                            >
                                                                <EditIcon/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="View Activity">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewUser(user)}
                                                            >
                                                                <ActivityIcon/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={filteredUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{flexShrink: 0}}
                    />
                </Paper>
            </Container>

            {/* User Details/Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <Avatar sx={{width: 40, height: 40}}>
                            {selectedUser?.username?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                            <Typography variant="h6">
                                {selectedUser?.username || 'Unknown User'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedUser?.email || selectedUser?.id}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="user dialog tabs">
                        <Tab
                            label="Profile Details"
                            icon={<EditIcon/>}
                            iconPosition="start"
                        />
                        <Tab
                            label="Activity Logs"
                            icon={<ActivityIcon/>}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                <DialogContent sx={{height: '60vh', overflow: 'hidden', p: 0}}>
                    {/* Activity Logs Tab */}
                    {activeTab === 0 && selectedUser && (
                        <ProfileDetails
                            ref={profileDetailsRef}
                            selectedUser={selectedUser}
                            onUserUpdate={handleUserUpdate}
                            apiClient={apiClient}
                        />
                    )}

                    {/* Profile Details Tab */}
                    {activeTab === 1 && selectedUser && (
                        <ActivityLogs
                            userLogs={userLogs}
                            loadingLogs={loadingLogs}
                            selectedUser={selectedUser}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;